import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FileText, Globe, Loader2, Trash2, Upload } from "lucide-react";

import {
  WECOM_VERIFY_FILE_MAX_BYTES,
  checkWeComVerifyFile,
  wecomVerifyFileService,
} from "@/entities/wecom-app";
import { ApiError } from "@/shared/api";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { useToast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";

const QUERY_KEY = ["wecom-verify-files"] as const;

type Props = {
  /** 读:TENANT_MANAGER 及以上(后端同口径)。 */
  canRead: boolean;
  /** 写(上传 / 删除):TENANT_ADMIN(平台代客时 effectiveRole 即 TENANT_ADMIN)。 */
  canWrite: boolean;
};

/**
 * 企微「域名验证文件」区块(OEM D1,域名方案 7.2;契约 = api d01cf328):
 *   - 域名指引:租户用自有域名(建议 h5.<客户域>)解析到平台入口,证书由入口按需签发;
 *   - WW_verify_*.txt 上传 / 列表(host + url)/ 删除:入库后后端按 Host 对 `/WW_verify_*.txt` 回内容。
 *   - 回调 host:列表接口回 callbackHosts —— 空 = 先去配回调域名(上传置灰);一个 = 不传 host;
 *     多个 = 必须下拉选定(不默认选第一个,防传错域名)。
 * 上传前本地先挡:文件名格式 + ≤128B。选完即清空 <input>,同名文件重选也能再次触发。409 = 同名已存在。
 */
export function WeComVerifyFilesSection({ canRead, canWrite }: Props) {
  const { t } = useTranslation("wecom");
  const { toast } = useToast();
  const confirm = useConfirm();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickedHost, setPickedHost] = useState("");

  const { data, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => wecomVerifyFileService.list(),
    enabled: canRead,
    meta: { suppressGlobalErrorToast: true },
  });
  const files = data?.files ?? [];
  const hosts = data?.callbackHosts ?? [];
  const multiHost = hosts.length > 1;
  const host = multiHost ? (hosts.includes(pickedHost) ? pickedHost : "") : null;
  const noHost = !!data && hosts.length === 0;

  const uploadMutation = useMutationWithToast({
    mutationFn: ({ file, host }: { file: File; host: string | null }) =>
      wecomVerifyFileService.upload(file, host),
    successToast: t("verifyFiles.uploadSuccess"),
    errorToast: (err, vars) =>
      err instanceof ApiError && err.httpStatus === 409
        ? {
            title: t("verifyFiles.duplicate"),
            description: t("verifyFiles.duplicateDesc", { fileName: vars.file.name }),
            variant: "destructive",
          }
        : t("verifyFiles.uploadFailed"),
    invalidateKeys: [[...QUERY_KEY]],
  });

  const deleteMutation = useMutationWithToast({
    mutationFn: (id: number) => wecomVerifyFileService.remove(id),
    successToast: t("verifyFiles.deleteSuccess"),
    errorToast: t("verifyFiles.deleteFailed"),
    invalidateKeys: [[...QUERY_KEY]],
  });

  const uploadDisabled = uploadMutation.isPending || !data || noHost || (multiHost && !host);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const problem = checkWeComVerifyFile(file);
    if (problem) {
      toast({
        title: t("verifyFiles.uploadFailed"),
        description: t(`verifyFiles.${problem}`, { max: WECOM_VERIFY_FILE_MAX_BYTES }),
        variant: "destructive",
      });
      return;
    }
    if (multiHost && !host) {
      toast({ title: t("verifyFiles.pickHostFirst"), variant: "destructive" });
      return;
    }
    uploadMutation.mutate({ file, host });
  };

  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-2xs font-medium uppercase tracking-wide text-muted-foreground/70">
          {t("verifyFiles.title")}
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            {multiHost && (
              <select
                aria-label={t("verifyFiles.pickHost")}
                value={host ?? ""}
                onChange={(e) => setPickedHost(e.target.value)}
                className="h-7 max-w-[14rem] rounded-md border bg-background px-2 text-2xs"
              >
                <option value="">{t("verifyFiles.pickHost")}</option>
                {hosts.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".txt,text/plain"
              className="hidden"
              data-testid="wecom-verify-file-input"
              onChange={onPick}
            />
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-2xs"
              disabled={uploadDisabled}
              onClick={() => inputRef.current?.click()}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Upload className="h-3 w-3 mr-1" />
              )}
              {uploadMutation.isPending ? t("verifyFiles.uploading") : t("verifyFiles.upload")}
            </Button>
          </div>
        )}
      </div>

      <p className="flex items-start gap-1.5 text-xs text-foreground/80 leading-relaxed">
        <Globe className="h-3.5 w-3.5 mt-0.5 shrink-0 text-info" />
        <span>{t("verifyFiles.domainGuide")}</span>
      </p>
      <p className="text-2xs text-muted-foreground leading-relaxed">{t("verifyFiles.hint")}</p>

      {!canRead ? (
        <p className="text-2xs text-muted-foreground">{t("verifyFiles.readonly")}</p>
      ) : isError ? (
        <p className="text-2xs text-destructive">{t("verifyFiles.loadFailed")}</p>
      ) : (
        <>
          {noHost && <p className="text-2xs text-warning">{t("verifyFiles.noCallbackHost")}</p>}
          {!canWrite && (
            <p className="text-2xs text-muted-foreground">{t("verifyFiles.viewOnly")}</p>
          )}
          {data && files.length === 0 ? (
            <p className="text-2xs text-muted-foreground">{t("verifyFiles.empty")}</p>
          ) : (
            <ul className="space-y-1.5" aria-label={t("verifyFiles.title")}>
              {files.map((f) => (
                <li key={f.id} className="flex items-start gap-2 text-xs">
                  <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="font-mono text-2xs truncate" title={f.fileName}>
                      {f.fileName}
                    </div>
                    <div className="text-2xs text-muted-foreground truncate">
                      {t("verifyFiles.hostLabel")}: <span className="font-mono">{f.host}</span>
                      {f.createdAt && (
                        <span className="ml-2">
                          {t("verifyFiles.uploadedAt", {
                            time: f.createdAt.replace("T", " ").slice(0, 16),
                          })}
                        </span>
                      )}
                    </div>
                    {f.url && (
                      <a
                        href={f.url}
                        target="_blank"
                        rel="noreferrer"
                        className="block font-mono text-2xs text-primary truncate hover:underline"
                        title={f.url}
                      >
                        {f.url}
                      </a>
                    )}
                  </div>
                  {canWrite && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-destructive"
                      disabled={deleteMutation.isPending}
                      aria-label={`${t("verifyFiles.delete")} ${f.fileName}`}
                      title={t("verifyFiles.delete")}
                      onClick={async () => {
                        const ok = await confirm({
                          title: t("verifyFiles.deleteConfirmTitle"),
                          description: t("verifyFiles.deleteConfirmDesc", {
                            fileName: f.fileName,
                          }),
                          confirmText: t("verifyFiles.delete"),
                          variant: "destructive",
                        });
                        if (ok) deleteMutation.mutate(f.id);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
